'use client';
import React, { useState, useEffect, useRef, ChangeEvent, FormEvent, ReactNode } from 'react';
import { CustomCheckbox } from '../components/forms/CustomCheckbox';

interface CardProps {
  children: ReactNode;
  className?: string;
}
const Card = ({ children, className = '' }: CardProps) => (
  <article className={`card rounded-2xl p-6 ${className}`}>{children}</article>
);

interface ChipProps {
  href: string;
  children: ReactNode;
}
const Chip = ({ href, children }: ChipProps) => (
  <a href={href} className="px-3 py-1 rounded-full hover:bg-white/25 text-sm" style={{ background: 'rgba(255,255,255,.14)' }}>
    {children}
  </a>
);

const INTEGRATIONS_DATA = [
  { name: 'Яндекс Календарь', cat: 'Calendar', type: 'native' },
  { name: 'Яндекс Диск', cat: 'Storage', type: 'native' },
  { name: 'Офис', cat: 'Tools', type: 'connector' },
  { name: 'Huntflow', cat: 'ATS', type: 'connector' },
  { name: 'AmoCRM', cat: 'CRM', type: 'connector' },
  { name: 'Hurma', cat: 'ATS', type: 'connector' },
  { name: 'ROBIN', cat: 'Automation', type: 'webhook' },
  { name: 'Битрикс24', cat: 'CRM', type: 'connector' },
];

export default function TechnologyPage() {
  const [filters, setFilters] = useState({ query: '', category: '', type: '' });
  const [formValues, setFormValues] = useState({ name: '', email: '', company: '', phone: '', message: '' });
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [isConsentChecked, setIsConsentChecked] = useState(false);

  const handleFilterChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { id, value } = e.target;
    setFilters(prev => ({ ...prev, [id]: value }));
  };

  const handleFormChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormValues(prev => ({ ...prev, [name]: value }));
  };

  const handleConsentChange = (e: ChangeEvent<HTMLInputElement>) => {
    setIsConsentChecked(e.target.checked);
  };

  const handleFormSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!isConsentChecked) {
      setFormStatus('error');
      return;
    }
    setFormStatus('submitting');

    const STRAPI_URL = `${process.env.NEXT_PUBLIC_STRAPI_API_URL}/api/contact-submissions`;

    try {
      const response = await fetch(STRAPI_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          data: {
            formName: 'Технологии',
            name: formValues.name,
            email: formValues.email,
            company: formValues.company,
            phone: formValues.phone,
            message: formValues.message,
          },
        }),
      });

      if (response.ok) {
        setFormStatus('success');
        setFormValues({ name: '', email: '', company: '', phone: '', message: '' });
        setIsConsentChecked(false);
      } else {
        const errorData = await response.json();
        console.error('Ошибка при отправке формы:', errorData);
        setFormStatus('error');
      }
    } catch (error) {
      console.error('Ошибка сети или сервера:', error);
      setFormStatus('error');
    }
  };

  const filteredItems = INTEGRATIONS_DATA.filter(x => {
    const okQ = !filters.query || x.name.toLowerCase().includes(filters.query.toLowerCase());
    const okC = !filters.category || x.cat === filters.category;
    const okT = !filters.type || x.type === filters.type;
    return okQ && okC && okT;
  });

  const countersRef = useRef<NodeListOf<Element> | null>(null);
  useEffect(() => {
    countersRef.current = document.querySelectorAll('.counter');
    const animate = (el: Element) => {
      const to = Number(el.getAttribute('data-to'));
      const duration = 900;
      const start = performance.now();
      const step = (now: DOMHighResTimeStamp) => {
        const progress = Math.min(1, (now - start) / duration);
        (el as HTMLElement).textContent = Math.floor(to * progress).toString();
        if (progress < 1) {
          requestAnimationFrame(step);
        }
      };
      requestAnimationFrame(step);
    };
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          animate(entry.target);
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.4 });
    if (countersRef.current) {
      countersRef.current.forEach(counter => observer.observe(counter));
    }
    return () => observer.disconnect();
  }, []);

  return (
    <div className="bg-gradient-to-br from-blue-900 to-blue-800 text-gray-100">
      <style>{`
        .btn-accent { background: #FFA500; color: #102a43; }
        .btn-accent:hover { filter: brightness(0.95); }
        .card { background: rgba(255,255,255,0.08); backdrop-filter: blur(8px); }
        .tag { background: rgba(255,255,255,0.2); padding: 0.25rem 0.75rem; border-radius: 9999px; font-size: 0.75rem; }
        .simple-card { background: rgba(255,255,255,0.08); padding: 1.5rem; border-radius: 1rem; }
      `}</style>

      <header
        className="px-6 pt-32 pb-16 md:pt-40 md:pb-20"
        style={{
          background: 'linear-gradient(rgba(0,0,0,.45), rgba(0,0,0,.25)), radial-gradient(1200px 600px at 70% -10%, rgba(255,255,255,.08), transparent)',
        }}
      >
        <div className="max-w-7xl mx-auto">
          <nav className="mb-6 flex flex-wrap gap-3 text-sm">
            <Chip href="#stack">Техстек</Chip>
            <Chip href="#integrations">Интеграции</Chip>
            <Chip href="#arch">Архитектура</Chip>
            <Chip href="#security">Безопасность</Chip>
            <Chip href="#perf">Производительность</Chip>
            <Chip href="#api">API & Webhooks</Chip>
            <Chip href="#devops">DevOps</Chip>
            <Chip href="#faq">FAQ</Chip>
            <Chip href="#contact">Связаться</Chip>
          </nav>
          <h1 className="text-4xl md:text-6xl font-extrabold mb-4">Технологии INTERIM</h1>
          <p className="max-w-3xl text-lg md:text-xl text-gray-200">
            Современный стек для быстрого и безопасного рекрутинга: Next.js, API-интеграции с ATS/CRM, BI-аналитика и надёжная инфраструктура.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <a href="#stack" className="px-6 py-3 rounded-xl font-semibold bg-white text-[#003B9B] border-2 border-[#003B9B] hover:brightness-105 transition-all">
              Посмотреть стек
            </a>
            <a href="#api" className="btn-accent px-6 py-3 rounded-xl font-semibold">
              Документация API
            </a>
          </div>
        </div>
      </header>

      <main className="px-6 py-12 md:py-16 max-w-7xl mx-auto space-y-16">
        <section id="stack" className="scroll-mt-32">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Техстек</h2>
          <div className="grid gap-6 md:grid-cols-4">
            <Card>
              <h3 className="text-xl font-semibold">Frontend</h3>
              <ul className="text-sm text-gray-200 mt-2 space-y-2">
                <li>• Next.js 14 (App Router)</li>
                <li>• TypeScript, React 18</li>
                <li>• Tailwind CSS, shadcn/ui</li>
                <li>• ISR/SSR, SEO/OG</li>
              </ul>
            </Card>
            <Card>
              <h3 className="text-xl font-semibold">Backend</h3>
              <ul className="text-sm text-gray-200 mt-2 space-y-2">
                <li>• Node.js (NestJS/Express)</li>
                <li>• REST + Webhooks</li>
                <li>• Auth: JWT/OAuth2</li>
                <li>• Rate-limit & Caching</li>
              </ul>
            </Card>
            <Card>
              <h3 className="text-xl font-semibold">Data</h3>
              <ul className="text-sm text-gray-200 mt-2 space-y-2">
                <li>• PostgreSQL (Prisma)</li>
                <li>• Redis (очки/кэш/очереди)</li>
                <li>• S3-совместимое хранилище</li>
                <li>• BI/отчёты: Dashboards</li>
              </ul>
            </Card>
            <Card>
              <h3 className="text-xl font-semibold">Инфраструктура</h3>
              <ul className="text-sm text-gray-200 mt-2 space-y-2">
                <li>• Vercel/Cloudflare + CDN</li>
                <li>• Docker, GitHub Actions</li>
                <li>• Мониторинг: Uptime/Logs</li>
                <li>• Backups & DR-план</li>
              </ul>
            </Card>
          </div>
        </section>

        <section id="integrations" className="scroll-mt-32">
          <div className="flex flex-wrap items-end gap-4 justify-between">
            <h2 className="text-3xl md:text-4xl font-bold">Интеграции и коннекторы</h2>
            <span id="i-count" className="text-sm text-gray-300">Всего: {filteredItems.length}</span>
          </div>
          <div className="mt-4 grid gap-3 md:grid-cols-4">
            <input
              id="query"
              value={filters.query}
              onChange={handleFilterChange}
              className="rounded-xl px-4 py-3 text-gray-900 md:col-span-2"
              placeholder="Поиск по интеграциям (ATS/CRM/Tools)…"
            />
            <select
              id="category"
              value={filters.category}
              onChange={handleFilterChange}
              className="rounded-xl px-4 py-3 text-gray-900"
            >
              <option value="">Категория: все</option>
              <option value="ATS">ATS</option>
              <option value="CRM">CRM</option>
              <option value="Calendar">Календарь</option>
              <option value="Mail">Почта</option>
              <option value="Storage">Файлы/медиа</option>
              <option value="Automation">Автоматизация</option>
              <option value="Tools">Tools</option>
            </select>
            <select
              id="type"
              value={filters.type}
              onChange={handleFilterChange}
              className="rounded-xl px-4 py-3 text-gray-900"
            >
              <option value="">Тип: все</option>
              <option value="native">Нативно</option>
              <option value="webhook">Webhooks</option>
              <option value="connector">Через коннектор</option>
            </select>
          </div>
          <div id="i-list" className="mt-6 grid gap-4 md:grid-cols-3">
            {filteredItems.length > 0 ? (
              filteredItems.map((item, index) => (
                <Card key={index} className="rounded-2xl p-5 flex items-center justify-between">
                  <div>
                    <div className="font-semibold">{item.name}</div>
                    <div className="text-xs text-gray-300">{item.cat}</div>
                  </div>
                  <span className="tag">
                    {item.type === 'native' ? 'Нативно' : item.type === 'webhook' ? 'Webhooks' : 'Коннектор'}
                  </span>
                </Card>
              ))
            ) : (
              <p className="col-span-3 text-gray-300">Интеграций не найдено — измените фильтры.</p>
            )}
          </div>
        </section>

        <section id="arch" className="scroll-mt-32">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Схема архитектуры</h2>
          <div className="card rounded-2xl p-6">
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <h3 className="font-semibold">Клиент/Edge</h3>
                <ul className="text-sm text-gray-200 mt-2 space-y-2">
                  <li>• CDN, WAF, кеширование</li>
                  <li>• SSR/ISR страницы</li>
                  <li>• Формы и загрузки файлов</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold">Приложение</h3>
                <ul className="text-sm text-gray-200 mt-2 space-y-2">
                  <li>• Next.js UI + API-роуты</li>
                  <li>• NestJS сервисы и очереди</li>
                  <li>• Вебхуки (ATS/CRM/почта)</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold">Данные/Инструменты</h3>
                <ul className="text-sm text-gray-200 mt-2 space-y-2">
                  <li>• PostgreSQL (Prisma)</li>
                  <li>• Redis (очки/кэш/очереди)</li>
                  <li>• S3-совместимое хранилище</li>
                  <li>• BI/отчёты: Dashboards</li>
                </ul>
              </div>
            </div>
            <img className="mt-6 w-full rounded-xl" src="https://placehold.co/1200x360?text=Architecture+Diagram" alt="Схема архитектуры" />
          </div>
        </section>

        <section id="security" className="scroll-mt-32">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Безопасность и соответствие</h2>
          <div className="grid gap-6 md:grid-cols-3">
            <Card>
              <h3 className="text-xl font-semibold">Данные и доступы</h3>
              <ul className="text-sm text-gray-200 mt-2 space-y-2">
                <li>• Шифрование в покое/транзите (TLS, AES-256)</li>
                <li>• RBAC, аудит действий</li>
                <li>• Регулярные бэкапы и тесты восстановления</li>
              </ul>
            </Card>
            <Card>
              <h3 className="text-xl font-semibold">Соответствие</h3>
              <ul className="text-sm text-gray-200 mt-2 space-y-2">
                <li>• 152-ФЗ: право на удаление/экспорт</li>
                <li>• Политики хранения и ретенции</li>
                <li>• DPIA для интеграций</li>
              </ul>
            </Card>
            <Card>
              <h3 className="text-xl font-semibold">Защита периметра</h3>
              <ul className="text-sm text-gray-200 mt-2 space-y-2">
                <li>• WAF, bot-защита, rate-limit</li>
                <li>• Secret-management (KMS/Env)</li>
                <li>• SAST/DAST в CI</li>
              </ul>
            </Card>
          </div>
        </section>

        <section id="perf" className="scroll-mt-32">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Производительность</h2>
          <div className="grid gap-6 md:grid-cols-4">
            <Card className="text-center">
              <div className="text-5xl font-extrabold counter" data-to="95">0</div>
              <div className="text-xs text-gray-300 mt-1">Производительность</div>
            </Card>
            <Card className="text-center">
              <div className="text-5xl font-extrabold counter" data-to="2">0</div>
              <div className="text-xs text-gray-300 mt-1">Время производительности, сек</div>
            </Card>
            <Card className="text-center">
              <div className="text-5xl font-extrabold counter" data-to="140">0</div>
              <div className="text-xs text-gray-300 mt-1">запросов/сек</div>
            </Card>
            <Card className="text-center">
              <div className="text-5xl font-extrabold counter" data-to="15">0</div>
              <div className="text-xs text-gray-300 mt-1">мс, Время до первого байта</div>
            </Card>
          </div>
        </section>

        <section id="api" className="scroll-mt-32">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">API и вебхуки</h2>
          <div className="grid gap-6 md:grid-cols-2">
            <Card className="p-0 overflow-hidden">
              <pre className="bg-black/35 rounded-xl p-4 overflow-auto m-0">
                <code className="text-sm text-gray-100 font-mono">
                  {`// Получить вакансии
GET /api/v1/vacancies?status=active&limit=20
// Создать кандидата
POST /api/v1/candidates
Content-Type: application/json
Authorization: Bearer <token>
{
  "fullName": "Иван Петров",
  "email": "ivan@example.com",
  "phone": "+7 900 000-00-00",
  "positionId": "vac_123",
  "source": "landing"
}`}
                </code>
              </pre>
            </Card>
            <Card className="p-0 overflow-hidden">
              <pre className="bg-black/35 rounded-xl p-4 overflow-auto m-0">
                <code className="text-sm text-gray-100 font-mono">
                  {`// Вебхук от ATS (пример)
POST /webhooks/ats
X-Signature: sha256=...
{
  "event": "candidate.status.changed",
  "candidateId": "cand_987",
  "status": "Interview",
  "vacancyId": "vac_123",
  "timestamp": 1699999999
}`}
                </code>
              </pre>
            </Card>
          </div>
          <p className="text-sm text-gray-300 mt-2">Поддержка idempotency-key, подпись HMAC, ретраи 3×.</p>
        </section>

        <section id="devops" className="scroll-mt-32">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">DevOps-процесс</h2>
          <div className="grid gap-6 md:grid-cols-3">
            <Card>
              <h3 className="text-xl font-semibold">Непрерывная интеграция (CI)</h3>
              <p className="text-sm text-gray-200 mt-1">
                GitHub Actions, JetBrains Space.
              </p>
            </Card>
            <Card>
              <h3 className="text-xl font-semibold">Развертывание (CD)</h3>
              <p className="text-sm text-gray-200 mt-1">
                Автоматический деплой на NetAngels.
              </p>
            </Card>
            <Card>
              <h3 className="text-xl font-semibold">Мониторинг</h3>
              <p className="text-sm text-gray-200 mt-1">
                Zabbix, Pingera.
              </p>
            </Card>
          </div>
        </section>

        <section id="faq" className="scroll-mt-32">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">FAQ</h2>
          <div className="space-y-3">
            <details className="simple-card">
              <summary className="cursor-pointer font-semibold">Как можно получить доступ к API?</summary>
              <p className="mt-2 text-sm text-gray-200">
                Запросите доступ у вашего аккаунт-менеджера. Мы вышлем ключ и документацию.
              </p>
            </details>
            <details className=" simple-card">
              <summary className="cursor-pointer font-semibold">Какие данные хранятся и как они защищены?</summary>
              <p className="mt-2 text-sm text-gray-200">
                Храним только необходимые для процесса найма данные. Всё шифруется
                и соответствует 152-ФЗ.
              </p>
            </details>
            <details className="simple-card">
              <summary className="cursor-pointer font-semibold">Можно ли настроить кастомную интеграцию?</summary>
              <p className="mt-2 text-sm text-gray-200">
                Да, для клиентов с тарифом Enterprise мы разрабатываем кастомные коннекторы.
              </p>
            </details>
          </div>
        </section>

        <section id="contact" className="scroll-mt-32">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Остались вопросы?</h2>
          <form className="simple-card grid gap-4 md:grid-cols-2" onSubmit={handleFormSubmit}>
            <input
              name="name"
              value={formValues.name}
              onChange={handleFormChange}
              className="rounded-xl px-4 py-3 text-gray-900"
              placeholder="Имя*"
              required
              disabled={formStatus === 'submitting'}
            />
            <input
              name="email"
              value={formValues.email}
              onChange={handleFormChange}
              className="rounded-xl px-4 py-3 text-gray-900"
              placeholder="E-mail*"
              type="email"
              required
              disabled={formStatus === 'submitting'}
            />
            <input
              name="company"
              value={formValues.company}
              onChange={handleFormChange}
              className="rounded-xl px-4 py-3 text-gray-900"
              placeholder="Компания"
              disabled={formStatus === 'submitting'}
            />
            <input
              name="phone"
              value={formValues.phone}
              onChange={handleFormChange}
              className="rounded-xl px-4 py-3 text-gray-900"
              placeholder="Телефон"
              disabled={formStatus === 'submitting'}
            />
            <textarea
              name="message"
              value={formValues.message}
              onChange={handleFormChange}
              className="rounded-xl px-4 py-3 text-gray-900 md:col-span-2"
              rows={4}
              placeholder="Коротко опишите задачу"
              disabled={formStatus === 'submitting'}
            />
            <button
              type="submit"
              className="btn-accent md:col-span-2 py-3 rounded-xl font-semibold"
              disabled={formStatus === 'submitting' || !isConsentChecked}
            >
              {formStatus === 'submitting' ? 'Отправка...' : 'Отправить'}
            </button>
            <CustomCheckbox
              name="consent"
              isChecked={isConsentChecked}
              onChange={handleConsentChange}
              link="/privacy"
              textColorClass="text-gray-400"
              linkColorClass="text-[#FF7C22]"
              className="md:col-span-2"
            />
            {formStatus === 'success' && (
              <p className="text-sm font-semibold text-green-400 md:col-span-2 text-center">
                Спасибо! Ваша заявка отправлена. Мы свяжемся с вами в ближайшее время.
              </p>
            )}
            {formStatus === 'error' && (
              <p className="text-sm font-semibold text-red-400 md:col-span-2 text-center">
                Произошла ошибка при отправке. Пожалуйста, попробуйте еще раз{!isConsentChecked ? ' и убедитесь, что вы согласились с обработкой данных.' : '.'}
              </p>
            )}
          </form>
        </section>
      </main>
    </div>
  );
}